import {
  type BusinessGroupRoleEntity,
  type BusinessGroupRoleEntityRoleEnum,
  type PermissionApi,
} from '@data-delivery/permission-ui-api';

export type UserRole = BusinessGroupRoleEntity;

export type RoleEnum = BusinessGroupRoleEntityRoleEnum;

type PermissionCheck<Key extends keyof Permissions> =
  | boolean
  | ((user: UserRole[], data: Permissions[Key]['dataType']) => boolean);

type RolesWithPermissions = Record<
  RoleEnum,
  Partial<{
    [Key in keyof Permissions]: Partial<Record<Permissions[Key]['action'], PermissionCheck<Key>>>;
  }>
>;

export type Permissions = {
  businessGroups: {
    dataType: PermissionApi.BusinessGroupEntity;
    action: 'view' | 'create' | 'update' | 'delete';
  };
  products: {
    dataType: PermissionApi.ProductEntity;
    action: 'view' | 'create' | 'update' | 'delete';
  };
  assignments: {
    dataType: PermissionApi.AssignmentEntity;
    action: 'view' | 'create' | 'update' | 'delete';
  };
  users: {
    dataType: PermissionApi.UserEntity & { businessGroup?: string };
    action: 'view' | 'create' | 'update' | 'delete';
  };
  configAssignments: {
    dataType: PermissionApi.AssignmentEntity;
    action: 'view' | 'create' | 'update' | 'delete';
  };
  configUsers: {
    dataType: PermissionApi.UserEntity & { businessGroup?: string };
    action: 'view' | 'create' | 'update' | 'delete';
  };
  directoryPermissions: {
    dataType: PermissionApi.DirectoryPermissionEntity;
    action: 'view' | 'create' | 'update' | 'delete';
  };
  contentAccessRoles: {
    dataType: PermissionApi.ContentAccessRoleEntity;
    action: 'view' | 'create' | 'update' | 'delete';
  };
  landingFolderRestrictions: {
    dataType: PermissionApi.LFREntity;
    action: 'view' | 'create' | 'update' | 'delete';
  };
  operatorUsers: {
    dataType: PermissionApi.OperatorUserEntity;
    action: 'view' | 'create' | 'update' | 'delete';
  };
  operatorGroups: {
    dataType: PermissionApi.OperatorGroupEntity;
    action: 'view' | 'create' | 'update' | 'delete';
  };
  retentions: {
    dataType: PermissionApi.RetentionEntity;
    action: 'view' | 'create' | 'update' | 'delete';
  };
};

function hasBusinessGroupAccess(user: UserRole[], businessGroupId: string | undefined): boolean {
  if (user.some(role => role.role === 'ADMIN' || role.role === 'OPERATOR')) {
    return true;
  }

  if (!businessGroupId) {
    return false;
  }

  return user.some(role => role.businessGroup === businessGroupId);
}

function canWriteToBusinessGroup(user: UserRole[], businessGroupId: string | undefined): boolean {
  if (user.some(role => role.role === 'ADMIN' || role.role === 'OPERATOR')) {
    return true;
  }

  if (!businessGroupId) {
    // If no specific BG is provided, check if user has SELF_SERVICE access to ANY BG
    return user.some(role => role.role === 'SELF_SERVICE' && role.businessGroup);
  }

  return user.some(role => role.role === 'SELF_SERVICE' && role.businessGroup === businessGroupId);
}

const ROLES = {
  ADMIN: {
    businessGroups: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    products: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    assignments: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    users: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    configAssignments: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    configUsers: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    directoryPermissions: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    contentAccessRoles: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    landingFolderRestrictions: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    operatorUsers: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    operatorGroups: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    retentions: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
  },
  OPERATOR: {
    businessGroups: {
      view: true,
      create: true,
      update: true,
      delete: false,
    },
    products: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    assignments: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    users: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    configAssignments: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    configUsers: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    directoryPermissions: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    contentAccessRoles: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    landingFolderRestrictions: {
      view: true,
      create: true,
      update: true,
      delete: true,
    },
    operatorUsers: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    operatorGroups: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    retentions: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
  },
  SELF_SERVICE: {
    businessGroups: {
      view: (user, data) => (data ? hasBusinessGroupAccess(user, data.id) : user.length > 0),
      create: false,
      update: false,
      delete: false,
    },
    products: {
      view: (user, data) =>
        data ? hasBusinessGroupAccess(user, data.businessGroup) : user.length > 0,
      create: (user, data) => canWriteToBusinessGroup(user, data?.businessGroup),
      update: (user, data) => canWriteToBusinessGroup(user, data?.businessGroup),
      delete: false,
    },
    assignments: {
      view: (user, data) =>
        data ? hasBusinessGroupAccess(user, data.businessGroup) : user.length > 0,
      create: (user, data) => canWriteToBusinessGroup(user, data?.businessGroup),
      update: (user, data) => canWriteToBusinessGroup(user, data?.businessGroup),
      delete: false,
    },
    users: {
      view: (user, data) =>
        data ? hasBusinessGroupAccess(user, data.businessGroup) : user.length > 0,
      create: (user, data) => canWriteToBusinessGroup(user, data?.businessGroup),
      update: (user, data) => canWriteToBusinessGroup(user, data?.businessGroup),
      delete: false,
    },
    configAssignments: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    configUsers: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    directoryPermissions: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    contentAccessRoles: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    landingFolderRestrictions: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    operatorUsers: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    operatorGroups: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    retentions: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
  },
  READ_ONLY: {
    businessGroups: {
      view: (user, data) => (data ? hasBusinessGroupAccess(user, data.id) : user.length > 0),
      create: false,
      update: false,
      delete: false,
    },
    products: {
      view: (user, data) =>
        data ? hasBusinessGroupAccess(user, data.businessGroup) : user.length > 0,
      create: false,
      update: false,
      delete: false,
    },
    assignments: {
      view: (user, data) =>
        data ? hasBusinessGroupAccess(user, data.businessGroup) : user.length > 0,
      create: false,
      update: false,
      delete: false,
    },
    users: {
      view: (user, data) =>
        data ? hasBusinessGroupAccess(user, data.businessGroup) : user.length > 0,
      create: false,
      update: false,
      delete: false,
    },
    configAssignments: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    configUsers: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    directoryPermissions: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    contentAccessRoles: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    landingFolderRestrictions: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    operatorUsers: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    operatorGroups: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
    retentions: {
      view: true,
      create: false,
      update: false,
      delete: false,
    },
  },
} as const satisfies RolesWithPermissions;

export function hasPermission<Resource extends keyof Permissions>(
  user: UserRole[],
  resource: Resource,
  action: Permissions[Resource]['action'],
  data?: Permissions[Resource]['dataType'],
): boolean {
  if (user.some(role => role.role === 'ADMIN')) {
    return true;
  }

  return user.some(role => {
    const permission = (ROLES as RolesWithPermissions)[role.role][resource]?.[action];

    if (permission == null) {
      return false;
    }

    if (typeof permission === 'boolean') {
      return permission;
    }

    return permission(user, data!);
  });
}

export function isOperator(user: UserRole[]): boolean {
  return user.some(role => role.role === 'ADMIN' || role.role === 'OPERATOR');
}

export function isFullOperator(user: UserRole[]): boolean {
  return user.some(role => role.role === 'ADMIN' || role.role === 'OPERATOR');
}

export function isSelfService(user: UserRole[]): boolean {
  return user.some(role => role.role === 'SELF_SERVICE');
}

export function isReadOnly(user: UserRole[]): boolean {
  return user.every(role => role.role === 'READ_ONLY');
}

export function getUserBusinessGroups(user: UserRole[]): string[] {
  if (isFullOperator(user)) {
    return ['*'];
  }

  const businessGroups = new Set<string>();
  for (const role of user) {
    if (role.businessGroup) {
      businessGroups.add(role.businessGroup);
    }
  }

  return Array.from(businessGroups);
}

export function getWritableBusinessGroups(user: UserRole[]): string[] {
  if (isFullOperator(user)) {
    return ['*'];
  }

  const writableGroups = new Set<string>();
  for (const role of user) {
    if (role.role === 'SELF_SERVICE' && role.businessGroup) {
      writableGroups.add(role.businessGroup);
    }
  }

  return Array.from(writableGroups);
}
